import { isApiError } from '../types/api-error';

/** Shape mínimo de erro Axios usado pelos interceptors. */
export interface AxiosErrorLike {
  response?: { status?: number; data?: { message?: string } };
  code?: string;
  message?: string;
  config?: { baseURL?: string; url?: string };
}

/**
 * Considera "erro de servidor" apenas quando há resposta 5xx ou timeout.
 * Não redireciona em "Network Error" (ex.: CORS bloqueado) para evitar
 * mandar o usuário para /service-unavailable em falhas de configuração.
 */
export const isServerError = (error: unknown): boolean => {
  const apiError = error as AxiosErrorLike;
  const status = apiError.response?.status;
  const has5xx = status === 502 || status === 503 || status === 504;
  const isTimeout = apiError.code === 'ECONNABORTED';
  const isNetworkError = apiError.message === 'Network Error';
  return has5xx || isTimeout || (isNetworkError && (has5xx || isTimeout));
};

/**
 * Redireciona para "serviço indisponível" apenas quando a falha foi da API principal (backend).
 * Falhas do backend-chatbot nunca devem redirecionar.
 */
export const isMainApiRequest = (error: unknown, mainApiBaseUrl: string): boolean => {
  const err = error as AxiosErrorLike;
  const url = err.config?.url ?? '';
  const fullUrl = typeof url === 'string' ? url : '';
  if (fullUrl.includes('/api/v1')) return false;
  const base = err.config?.baseURL;
  if (!base) return true;
  return base === mainApiBaseUrl;
};

export const isAuthRoute = (url: string | undefined): boolean => {
  const skipRefreshRoutes = ['/auth/login', '/auth/logout', '/auth/refresh', '/auth/register'];
  return skipRefreshRoutes.some((route) => url?.includes(route));
};

export const getErrorMessage = (error: unknown): string => {
  if (isApiError(error)) {
    return error.response?.data?.message || error.message || 'Erro ao renovar token';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Erro ao renovar token';
};

export type ResponseInterceptorDecision =
  | { type: 'handle_server_error' }
  | { type: 'handle_permission_error' }
  | { type: 'handle_token_refresh' }
  | { type: 'reject' };

interface OriginalRequestLike {
  _retry?: boolean;
  url?: string;
}

/**
 * Decisão pura do interceptor de resposta (sem efeitos colaterais).
 * Mantém a mesma ordem de ramos que `api.ts`.
 */
export const getResponseInterceptorDecision = (
  error: unknown,
  originalRequest: OriginalRequestLike | undefined
): ResponseInterceptorDecision => {
  if (isServerError(error)) {
    return { type: 'handle_server_error' };
  }

  const status = (error as AxiosErrorLike).response?.status;
  if (status === 403) {
    return { type: 'handle_permission_error' };
  }

  if (status === 401 && !originalRequest?._retry && !isAuthRoute(originalRequest?.url)) {
    return { type: 'handle_token_refresh' };
  }

  return { type: 'reject' };
};

interface PermissionError extends Error {
  permissionDenied?: boolean;
  permissionMessage?: string;
  response?: { data?: { message?: string } };
}

/** Enriquece o erro com flags usadas pela UI (mesmo comportamento do interceptor). */
export const enrichPermissionDeniedError = (error: unknown): void => {
  const apiError = error as PermissionError;
  const errorMessage = apiError.response?.data?.message || 'Permissão insuficiente.';
  apiError.permissionDenied = true;
  apiError.permissionMessage = errorMessage;
};
